import { useMemo } from 'react'
import { useAppSelector } from '@store/hooks'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '../../types/dashboardMetrics.types'
import type { SchedulingOverviewResult } from '@services/api/dashboard/scheduling.api'
import { mapSchedulingOverviewToSecondaryMetrics } from '@utils/overviewAppointments.util'
import { getFullName } from '@utils/fullname.util'
import {
  getMockOperatoriesForOverview,
  getProviderUtilizationRows,
} from '@components/pages/Dashboard/Overview/dashboardOverview.mockData'
import { getOverviewUtilizationRow1Metrics } from '@components/pages/Dashboard/Overview/overviewMetrics.mockData'
import { useClinicOperatories } from '@hooks/useClinicOperatories'
import { useClinicProviders } from '@hooks/useClinicProviders'
import { useSchedulingOverview } from './useSchedulingOverview'
import { useProviderUtilizationOverview } from './useProviderUtilizationOverview'
import { useClinicalEfficiencyOverview } from './useClinicalEfficiencyOverview'
import { buildClinicalEfficiencyMetrics } from '@utils/clinicalEfficiency.util'
import { useDiagnosedTreatmentOverview } from './useDiagnosedTreatmentOverview'
import { useCaseAcceptanceOverview } from './useCaseAcceptanceOverview'
import { buildUtilizationRow2Metrics } from '@utils/treatmentAcceptance.util'
import type { CaseAcceptanceOverviewResult } from '@services/api/dashboard/case-acceptance.api'
import type { ProviderUtilizationOverviewRow } from '@services/api/dashboard/provider-utilization.api'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

const APPOINTMENT_SECONDARY_METRIC_IDS = new Set([
  'hygiene-util',
  'same-day-tx',
  'pending-tx-value',
  'new-patients',
])

const PERFORMANCE_UTILIZATION_METRIC_IDS = new Set([
  'provider-util',
  'hygiene-util',
  'same-day-tx',
  'pending-tx-value',
  'new-patients',
])

export type UtilizationFetchFlags = {
  scheduling: boolean
  providerUtilization: boolean
  clinicalEfficiency: boolean
  diagnosedTreatment: boolean
  caseAcceptance: boolean
}

type UseDashboardUtilizationDataOptions = {
  range: RangeKey
  date: string
  fetchFlags?: UtilizationFetchFlags
  /** Sections that are visible but still waiting for their fetch wave. */
  queuedFlags?: Partial<UtilizationFetchFlags>
  /**
   * Overview only: false holds the scheduling fetch (pass 0) until the list
   * settles; true fetches with Math.max(count, 1) so an empty clinic still
   * requests once. Omitted (performance): only fetch when count > 0.
   */
  operatoriesSettled?: boolean
}

const DEFAULT_FLAGS: UtilizationFetchFlags = {
  scheduling: true,
  providerUtilization: true,
  clinicalEfficiency: true,
  diagnosedTreatment: true,
  caseAcceptance: true,
}

export type DashboardUtilizationData = {
  schedulingOverview: SchedulingOverviewResult | null
  /** Params for which schedulingOverview is valid; null when data is cleared. */
  schedulingOverviewDataParams: ReturnType<
    typeof useSchedulingOverview
  >['dataParams']
  schedulingLoading: boolean
  /** True once the scheduling overview response has arrived. */
  schedulingHasPayload: boolean
  appointmentSecondaryMetrics: DashboardMetricItemData[]
  schedulingUtilizationMetrics: DashboardMetricItemData[]
  clinicalEfficiencyMetrics: DashboardMetricItemData[]
  clinicalEfficiencyLoading: boolean
  clinicalEfficiencyHasData: boolean
  /** True once the clinical efficiency response has arrived. */
  clinicalEfficiencyHasPayload: boolean
  utilizationRow2Metrics: DashboardMetricItemData[]
  diagnosedTreatmentLoading: boolean
  /** True once the diagnosed treatment response has arrived. */
  diagnosedTreatmentHasPayload: boolean
  caseAcceptanceOverview: CaseAcceptanceOverviewResult | null
  caseAcceptanceLoading: boolean
  /** True once the case acceptance response has arrived. */
  caseAcceptanceHasPayload: boolean
  providerUtilizationRows: ProviderUtilizationOverviewRow[]
  providerUtilizationLoading: boolean
  /** True once the provider utilization response has arrived. */
  providerUtilizationHasPayload: boolean
  mergeRow1SchedulingMetrics: (
    mockMetrics: DashboardMetricItemData[],
  ) => DashboardMetricItemData[]
  operatoryCount: number
}

export function mergeRow1SchedulingMetrics(
  mockMetrics: DashboardMetricItemData[],
  liveMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  const liveById = new Map(
    liveMetrics
      .filter((metric) => metric.id)
      .map((metric) => [metric.id as string, metric]),
  )
  return mockMetrics.map((metric) =>
    metric.id && liveById.has(metric.id)
      ? (liveById.get(metric.id) as DashboardMetricItemData)
      : metric,
  )
}

function buildSchedulingSecondaryMetrics(
  range: RangeKey,
  schedulingOverview: ReturnType<typeof useSchedulingOverview>['data'],
  metricIds: Set<string>,
  useMocks: boolean,
): DashboardMetricItemData[] {
  const shells = withDashboardMetricShellValues(
    getOverviewUtilizationRow1Metrics(range).filter((metric) =>
      metricIds.has(metric.id ?? ''),
    ),
    useMocks,
  )
  if (!schedulingOverview) {
    return shells
  }
  return mapSchedulingOverviewToSecondaryMetrics(schedulingOverview, shells)
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function useDashboardUtilizationDataImpl({
  range,
  date,
  fetchFlags = DEFAULT_FLAGS,
  queuedFlags,
  operatoriesSettled,
}: UseDashboardUtilizationDataOptions) {
  useClinicOperatories()
  useClinicProviders()

  const useMocks = isDashboardDevMocksEnabled()
  const providersFromStore = useAppSelector((state) => state.provider.data)
  const operatoryList = useAppSelector((state) => state.operatory.data)
  const displayOperatoryList =
    (operatoryList?.length ?? 0) > 0
      ? (operatoryList ?? [])
      : useMocks
        ? getMockOperatoriesForOverview()
        : []
  const operatoryCount = displayOperatoryList.length
  // 0 = hold request (useSchedulingOverview). Overview floors to 1 after settle
  // so count 0 still fetches; performance (omit settled) waits for count > 0.
  const schedulingOperatoryCount =
    operatoriesSettled === undefined
      ? operatoryCount > 0
        ? operatoryCount
        : 0
      : operatoriesSettled
        ? Math.max(operatoryCount, 1)
        : 0

  const {
    data: schedulingOverview,
    dataParams: schedulingOverviewDataParams,
    loading: schedulingLoading,
  } = useSchedulingOverview(
    range,
    date,
    schedulingOperatoryCount,
    fetchFlags.scheduling,
    queuedFlags?.scheduling ?? false,
  )
  const {
    data: providerUtilizationOverview,
    loading: providerUtilizationLoading,
  } = useProviderUtilizationOverview(
    range,
    date,
    fetchFlags.providerUtilization,
    queuedFlags?.providerUtilization ?? false,
  )
  const {
    data: clinicalEfficiencyOverview,
    loading: clinicalEfficiencyLoading,
  } = useClinicalEfficiencyOverview(
    range,
    date,
    fetchFlags.clinicalEfficiency,
    queuedFlags?.clinicalEfficiency ?? false,
  )
  const {
    data: diagnosedTreatmentOverview,
    loading: diagnosedTreatmentLoading,
  } = useDiagnosedTreatmentOverview(
    range,
    date,
    fetchFlags.diagnosedTreatment,
    queuedFlags?.diagnosedTreatment ?? false,
  )
  const { data: caseAcceptanceOverview, loading: caseAcceptanceLoading } =
    useCaseAcceptanceOverview(
      range,
      date,
      fetchFlags.caseAcceptance,
      queuedFlags?.caseAcceptance ?? false,
    )

  const providerNames = useMemo((): string[] => {
    return (providersFromStore ?? [])
      .filter((provider) => !provider.is_hidden)
      .map((provider) => getFullName(provider) || provider.username)
      .filter((name): name is string => Boolean(name))
  }, [providersFromStore])

  const appointmentSecondaryMetrics = useMemo(
    () =>
      buildSchedulingSecondaryMetrics(
        range,
        schedulingOverview,
        APPOINTMENT_SECONDARY_METRIC_IDS,
        useMocks,
      ),
    [range, schedulingOverview, useMocks],
  )

  const schedulingUtilizationMetrics = useMemo(
    () =>
      buildSchedulingSecondaryMetrics(
        range,
        schedulingOverview,
        PERFORMANCE_UTILIZATION_METRIC_IDS,
        useMocks,
      ),
    [range, schedulingOverview, useMocks],
  )

  const clinicalEfficiencyMetrics = useMemo(
    () =>
      buildClinicalEfficiencyMetrics(range, clinicalEfficiencyOverview?.kpis),
    [range, clinicalEfficiencyOverview?.kpis],
  )

  const utilizationRow2Metrics = useMemo(
    () =>
      buildUtilizationRow2Metrics(
        range,
        diagnosedTreatmentOverview?.kpis,
        caseAcceptanceOverview?.kpis,
      ),
    [range, diagnosedTreatmentOverview?.kpis, caseAcceptanceOverview?.kpis],
  )

  const liveRow1SchedulingMetrics = useMemo(
    () =>
      buildSchedulingSecondaryMetrics(
        range,
        schedulingOverview,
        APPOINTMENT_SECONDARY_METRIC_IDS,
        useMocks,
      ),
    [range, schedulingOverview, useMocks],
  )

  const mergeRow1SchedulingMetricsFn = useMemo(
    () =>
      (mockMetrics: DashboardMetricItemData[]): DashboardMetricItemData[] =>
        mergeRow1SchedulingMetrics(mockMetrics, liveRow1SchedulingMetrics),
    [liveRow1SchedulingMetrics],
  )

  const providerUtilizationRows =
    useMemo((): ProviderUtilizationOverviewRow[] => {
      if (providerUtilizationOverview?.rows?.length) {
        return providerUtilizationOverview.rows
      }
      if (!useMocks) {
        return []
      }
      return getProviderUtilizationRows(
        range,
        providerNames.length > 0 ? providerNames : undefined,
      )
    }, [providerUtilizationOverview?.rows, range, providerNames, useMocks])

  return {
    schedulingOverview,
    schedulingOverviewDataParams,
    schedulingLoading: fetchFlags.scheduling && schedulingLoading,
    schedulingHasPayload: schedulingOverview != null,
    appointmentSecondaryMetrics,
    schedulingUtilizationMetrics,
    clinicalEfficiencyMetrics,
    clinicalEfficiencyLoading:
      fetchFlags.clinicalEfficiency && clinicalEfficiencyLoading,
    // Displayed Clinical efficiency tiles only. The API returns formatted
    // strings (e.g. "1 hr 30 mins"); the empty-state parser handles them.
    clinicalEfficiencyHasData: hasNonZeroDashboardValues(
      clinicalEfficiencyOverview?.kpis.avgTimePerPatient,
      clinicalEfficiencyOverview?.kpis.avgProviderUtilization,
      clinicalEfficiencyOverview?.kpis.treatmentOpportunities,
    ),
    clinicalEfficiencyHasPayload: clinicalEfficiencyOverview != null,
    utilizationRow2Metrics,
    diagnosedTreatmentLoading:
      fetchFlags.diagnosedTreatment && diagnosedTreatmentLoading,
    diagnosedTreatmentHasPayload: diagnosedTreatmentOverview != null,
    caseAcceptanceOverview: caseAcceptanceOverview ?? null,
    caseAcceptanceLoading: fetchFlags.caseAcceptance && caseAcceptanceLoading,
    caseAcceptanceHasPayload: caseAcceptanceOverview != null,
    providerUtilizationRows,
    providerUtilizationLoading:
      fetchFlags.providerUtilization && providerUtilizationLoading,
    providerUtilizationHasPayload: providerUtilizationOverview != null,
    mergeRow1SchedulingMetrics: mergeRow1SchedulingMetricsFn,
    operatoryCount,
  }
}

export function useDashboardUtilizationData(
  options: UseDashboardUtilizationDataOptions,
): DashboardUtilizationData {
  return useDashboardUtilizationDataImpl(options)
}
